import React, { useEffect, useState, useCallback } from 'react';
import FarmCard from './FarmCard';

/**
 * Dashboard showing all farms with their last test run status.
 * Each farm is displayed as a card with the latest run information.
 */
export default function Dashboard({ api, onSelectFarm }) {
  const [farms, setFarms] = useState([]);
  const [farmStatuses, setFarmStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [nextRefreshIn, setNextRefreshIn] = useState(30);

  const AUTO_REFRESH_SECONDS = 30;

  const loadDashboard = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError('');
    
    try {
      // First, get the list of farms
      const { farms: farmList } = await api.listFarms();
      setFarms(farmList || []);
      
      // Then, fetch the last run for each farm
      const statusPromises = (farmList || []).map(async (farm) => {
        try {
          const { runs } = await api.listRuns(farm.code, 1);
          return { 
            code: farm.code, 
            lastRun: runs && runs.length > 0 ? runs[0] : null 
          };
        } catch (e) {
          console.error(`Failed to load status for ${farm.code}:`, e.message);
          return { code: farm.code, lastRun: null };
        }
      });
      
      const statuses = await Promise.all(statusPromises);
      const statusMap = {};
      statuses.forEach(({ code, lastRun }) => {
        statusMap[code] = lastRun;
      });
      
      setFarmStatuses(statusMap);
      setLastUpdated(new Date());
      setNextRefreshIn(AUTO_REFRESH_SECONDS);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadDashboard(true);
    
    // Refresh dashboard every 30 seconds
    const interval = setInterval(() => loadDashboard(false), AUTO_REFRESH_SECONDS * 1000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev <= 1 ? AUTO_REFRESH_SECONDS : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatLastUpdated = (date) => {
    if (!date) return '—';
    return date.toLocaleTimeString();
  };

  if (loading && farms.length === 0) {
    return <div className="card">Loading dashboard…</div>;
  }

  if (error && farms.length === 0) {
    return <div className="error card">{error}</div>;
  }

  if (!farms.length) {
    return <div className="card">No farms with stored credentials are available.</div>;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Farm Status Dashboard</h2>
        <div className="dashboard-actions">
          <div className="dashboard-meta">
            <span>Last updated: {formatLastUpdated(lastUpdated)}</span>
            <span>Auto refresh in {nextRefreshIn}s</span>
          </div>
          <button className="link-btn" onClick={() => loadDashboard(true)} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh now'}
          </button>
        </div>
      </div>
      
      {error && <div className="error card">{error}</div>}
      
      <div className="farm-grid">
        {farms.map((farm) => (
          <FarmCard
            key={farm.code}
            farm={farm}
            lastRun={farmStatuses[farm.code]}
            onSelect={() => onSelectFarm(farm.code)}
          />
        ))}
      </div>
    </div>
  );
}
